import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import Row from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { useMemo } from 'react'
import { Check } from 'react-feather'
import styled from 'styled-components'
import { ThemedText } from 'theme'

import TransactionError from '../../TransactionError'

const StyledListItem = styled(Row)`
  padding: 8px 0;
  gap: 12px;
  align-items: flex-start;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
  }
`

const CheckIcon = styled(Check)`
  color: ${({ theme }) => theme.accentSuccess};
  background: ${({ theme }) => `${theme.accentSuccess}1A`}; // 10% opacity
  padding: 3px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-top: 2px;
`

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0 0 16px 0;
  line-height: 1.5;
`

const TokenDisplay = styled(Row)`
  background: ${({ theme }) => theme.cardTertiary};
  border-radius: 12px;
  padding: 12px;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`

const StepText = styled(ThemedText.DeprecatedBody)`
  font-size: 14px;
  font-weight: 500;
`

const StepDescription = styled(ThemedText.DeprecatedSmall)`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`

type RewardVaultStakeStepProps = {
  next: () => void
  onStart: () => Promise<string | null>
  totalStakedAmount: string
  stakingToken: any
  rewardVault: string | null
  rewardVaultRewardSymbol?: string
  error?: string
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

export default function RewardVaultStakeStep({
  next,
  onStart,
  totalStakedAmount,
  stakingToken,
  rewardVault,
  rewardVaultRewardSymbol,
  error,
  ...rest
}: RewardVaultStakeStepProps) {
  const rewardVaultRewardsLabel = rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} rewards` : 'rewards'
  // Format token amount for display
  const formattedAmount = useMemo(() => {
    if (!stakingToken || !totalStakedAmount) return '0'

    try {
      const decimals = stakingToken.decimals
      const divisor = BigInt(10) ** BigInt(decimals)
      const tokens = BigInt(totalStakedAmount)

      const integerPart = tokens / divisor
      const fractionalPart = tokens % divisor

      let fractionalStr = fractionalPart.toString().padStart(decimals, '0')
      fractionalStr = fractionalStr.replace(/0+$/, '')

      if (fractionalStr.length > 0) {
        return `${integerPart}.${fractionalStr}`
      } else {
        return integerPart.toString()
      }
    } catch (e) {
      console.error('Error formatting token amount:', e)
      return totalStakedAmount
    }
  }, [stakingToken, totalStakedAmount])

  const hasAmount = useMemo(() => totalStakedAmount && BigInt(totalStakedAmount) > BigInt(0), [totalStakedAmount])

  return (
    <ConfirmationTransactionStep {...rest} onConfirm={next}>
      <Column width="100%" gap="16px">
        <Description>
          Finally, stake your tokens in the Reward Vault to start earning {rewardVaultRewardsLabel} and support the
          Berachain network.
        </Description>

        <LightCard>
          <ThemedText.DeprecatedSubHeader style={{ padding: '4px 0 8px', fontSize: '16px' }}>
            Staking in Reward Vault
          </ThemedText.DeprecatedSubHeader>

          <StyledListItem>
            <CheckIcon />
            <Column gap="2px">
              <StepText>Stake in Reward Vault</StepText>
              <StepDescription>Start earning {rewardVaultRewardsLabel}</StepDescription>
            </Column>
          </StyledListItem>
        </LightCard>

        {hasAmount && (
          <TokenDisplay>
            <CurrencyLogo currency={stakingToken} size="24px" />
            <Column>
              <ThemedText.DeprecatedBody fontWeight={500} fontSize="16px">
                {formattedAmount} {stakingToken?.symbol}
              </ThemedText.DeprecatedBody>
              <StepDescription>To be staked in Reward Vault</StepDescription>
            </Column>
          </TokenDisplay>
        )}

        <ButtonPrimary onClick={onStart} disabled={!hasAmount}>
          {hasAmount ? 'Stake in Reward Vault' : 'No Tokens to Stake'}
        </ButtonPrimary>
        {error && <TransactionError error={error} />}
      </Column>
    </ConfirmationTransactionStep>
  )
}
